<template>
  <v-dialog fullscreen v-model="showDialog">
    <v-card>
      <v-card-title class="secondary white--text mb-4 rounded-0">
        Sub Hire Entries — {{ date }}
        <v-spacer />
        <v-btn
          small
          depressed
          color="white"
          class="mr-2"
          @click="exportXLSX"
        >
          <v-icon left small color="secondary">mdi-file-excel-outline</v-icon>
          <span class="secondary--text">Export</span>
        </v-btn>
        <v-btn icon @click="showDialog = false">
          <v-icon class="white--text">mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        <!-- Summary Chips -->
        <v-row class="mb-3 mx-0" v-if="allRowList.length">
          <v-col cols="auto" class="pa-1">
            <v-chip color="primary" text-color="white" small>
              <v-icon left small>mdi-truck-outline</v-icon>
              Vendors: {{ allRowList.length }}
            </v-chip>
          </v-col>
          <v-col cols="auto" class="pa-1">
            <v-chip color="blue darken-1" text-color="white" small>
              <v-icon left small>mdi-handshake-outline</v-icon>
              Total Committed: {{ totalCommitted }}
            </v-chip>
          </v-col>
          <v-col cols="auto" class="pa-1">
            <v-chip color="teal" text-color="white" small>
              <v-icon left small>mdi-check-circle-outline</v-icon>
              Total Allocated: {{ totalAllocated }}
            </v-chip>
          </v-col>
          <v-col cols="auto" class="pa-1">
            <v-chip color="orange darken-1" text-color="white" small>
              <v-icon left small>mdi-progress-check</v-icon>
              Remaining: {{ totalCommitted - totalAllocated }}
            </v-chip>
          </v-col>
          <v-col cols="auto" class="pa-1">
            <v-chip :color="allocationColor" text-color="white" small>
              <v-icon left small>mdi-percent-outline</v-icon>
              Utilization: {{ allocationPercentage }}%
            </v-chip>
          </v-col>
        </v-row>

        <AgGridVue
          style="width: 100%; height: calc(100vh - 260px)"
          class="ag-theme-alpine"
          :columnDefs="headers"
          :context="context"
          @grid-ready="onGridReady"
          @columnMoved="resizeGrid"
          :defaultColDef="defaultColDef"
          :grid-options="gridOptions"
          :rowData="allRowList"
        />
        <v-col v-if="totalItems > 0" cols="12" class="d-flex justify-end">
          <Pagination
            :pageNo="pageNo"
            :totalItems="totalItems"
            :pageSize="itemsPerPage"
            @itemsPerPageChange="itemsPerPageChanged"
            @prevPage="prevPage"
            @nextPage="nextPage"
          />
        </v-col>
      </v-card-text>
    </v-card>
    <SubHireCommitmentEntryForm
      v-model="showEntryForm"
      :entryId="selectedEntryId"
      @refreshList="getEntryList()"
    />
    <SubHireCommitmentLogDialog
      v-model="showLogDialog"
      :entryId="selectedEntryId"
    />
  </v-dialog>
</template>
<script>
import { AgGridVue } from "ag-grid-vue";
import Pagination from "@/components/General/Pagination.vue";
import SubHireCommitmentEntryButton from "@/components/AgGridComponents/SubHireCommitmentEntryButton.vue";
import SubHireCommitmentEntryForm from "@/components/ModuleBased/dialogForms/SubHireCommitmentEntryForm.vue";
import SubHireCommitmentLogDialog from "@/components/ModuleBased/dialogs/SubHireCommitmentLogDialog.vue";

export default {
  components: {
    AgGridVue,
    Pagination,
    SubHireCommitmentEntryButton,
    SubHireCommitmentEntryForm,
    SubHireCommitmentLogDialog,
  },
  props: {
    value: Boolean,
    date: { type: String, default: null },
  },
  data() {
    return {
      allRowList: [],
      showEntryForm: false,
      showLogDialog: false,
      selectedEntryId: null,
      pageNo: 1,
      itemsPerPage: 20,
      totalItems: 0,
      defaultColDef: { resizable: true },
      gridOptions: {
        onGridSizeChanged: () => this.resizeGrid(),
        suppressRowClickSelection: true,
        enableCellTextSelection: true,
        rowHeight: 35,
      },
      headers: [
        { headerName: "Transporter", field: "transporter_name", minWidth: 200 },
        { headerName: "Committed", field: "committed", minWidth: 120 },
        { headerName: "Allocated", field: "allocated", minWidth: 120 },
        { headerName: "Created By", field: "created_by", minWidth: 140 },
        {
          headerName: "Actions",
          field: "actions",
          cellRendererFramework: "SubHireCommitmentEntryButton",
          pinned: "right",
          width: 120,
        },
      ],
    };
  },
  computed: {
    showDialog: {
      get() { return this.value; },
      set(v) { this.$emit("input", v); },
    },
    context() { return { parentComponent: this }; },
    offset() { return this.itemsPerPage * (this.pageNo - 1); },
    totalCommitted() {
      return this.allRowList.reduce((sum, r) => sum + (Number(r.committed) || 0), 0);
    },
    totalAllocated() {
      return this.allRowList.reduce((sum, r) => sum + (Number(r.allocated) || 0), 0);
    },
    allocationPercentage() {
      if (!this.totalCommitted) return 0;
      return Math.round((this.totalAllocated / this.totalCommitted) * 100);
    },
    allocationColor() {
      if (this.allocationPercentage >= 90) return "success";
      if (this.allocationPercentage >= 50) return "orange darken-1";
      return "error";
    },
  },
  watch: {
    value(val) {
      if (val) {
        this.pageNo = 1;
        this.getEntryList();
      } else {
        this.allRowList = [];
        this.totalItems = 0;
      }
    },
  },
  methods: {
    exportXLSX() {
      const XLSX = require("xlsx");
      const rows = this.allRowList.map((r) => ({
        Transporter: r.transporter_name,
        Committed: r.committed,
        Allocated: r.allocated,
        Remaining: (Number(r.committed) || 0) - (Number(r.allocated) || 0),
        "Utilization %": r.committed
          ? Math.round(((Number(r.allocated) || 0) / Number(r.committed)) * 100)
          : 0,
        "Created By": r.created_by,
      }));

      const summary = [
        {
          Transporter: "TOTAL",
          Committed: this.totalCommitted,
          Allocated: this.totalAllocated,
          Remaining: this.totalCommitted - this.totalAllocated,
          "Utilization %": this.allocationPercentage,
          "Created By": "",
        },
      ];

      const ws = XLSX.utils.json_to_sheet([...rows, {}, ...summary]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Sub Hire Entries");
      XLSX.writeFile(wb, `sub-hire-entries-${this.date}.xlsx`);
    },
    editCommitmentEntry(id) {
      this.selectedEntryId = id;
      setTimeout(() => { this.showEntryForm = true; }, 100);
    },
    viewCommitmentLogs(id) {
      this.selectedEntryId = id;
      this.showLogDialog = true;
    },
    resizeGrid() {
      if (this.gridOptions?.api) {
        setTimeout(() => this.gridOptions.api.sizeColumnsToFit(), 100);
      }
    },
    onGridReady() { this.resizeGrid(); },
    itemsPerPageChanged(e) { this.pageNo = 1; this.itemsPerPage = e; this.getEntryList(); },
    prevPage() { this.pageNo--; this.getEntryList(); },
    nextPage() { this.pageNo++; this.getEntryList(); },
    getEntryList() {
      if (!this.date) return;
      this.$bus.$emit("showLoader", true);
      const params = { offset: this.offset, limit: this.itemsPerPage, date: this.date };
      this.$api.subHireCommitment
        .getSubHireCommitmentEntryList(params)
        .then((res) => {
          this.allRowList = res.data;
          this.totalItems = res.count;
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },
  },
};
</script>
